import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../globalStyles/fonts";
import { toast } from "react-toastify";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { inventoryConfigService } from "../../utils/services/inventory-config.service";
import { wmsService } from "../../utils/services/wmsProvider";
import { productService } from "../../utils/services/productService";

const initData = {
  safety_stock_count: "",
  avg_fullfillment_days: "",
  warehouse: null,
  avg_fullfillment_days_supplier: "",
  sku: "",
  min_order_quantity: "",
  is_active: true,
};

const labelStyle = {
  mb: 1,
  fontSize: "0.875rem",
  fontFamily: "Poppins",
  fontWeight: 400,
  color: "var(--text-heading)",
};

const InventoryConfigModal = ({
  open,
  handleClose,
  onAddData,
  selectedInventoryConfig,
  lockedProduct = null,
}) => {
  const [formData, setFormData] = useState(initData);
  const [formErrors, setFormErrors] = useState({});
  const [fetchedProduct, setFetchedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const clientId = localStorage.getItem("client_id");

  // Fetch full product data on mount or when lockedProduct changes
  useEffect(() => {
    const fetchProductData = async () => {
      if (lockedProduct?._id) {
        try {
          setLoading(true);
          const product = await productService.fetchById(lockedProduct._id);
          setFetchedProduct(product);
        } catch (error) {
          console.error("Error fetching product:", error);
          setFetchedProduct(lockedProduct);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchProductData();
  }, [lockedProduct?._id]);

  useEffect(() => {
    if (selectedInventoryConfig) {
      setFormData({
        ...selectedInventoryConfig,
        sku: { sku: selectedInventoryConfig?.sku } || "",
        is_active: selectedInventoryConfig?.is_active ?? true,
        safety_stock_count: selectedInventoryConfig?.safety_stock_count || "",
        avg_fullfillment_days:
          selectedInventoryConfig?.avg_fullfillment_days || "",
        avg_fullfillment_days_supplier:
          selectedInventoryConfig?.avg_fullfillment_days_supplier || "",
        min_order_quantity: selectedInventoryConfig?.min_order_quantity || "",
      });
    } else {
      setFormData(initData);
    }
  }, [selectedInventoryConfig]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!fetchedProduct) {
      errors.general = "Product context is missing.";
    }
    if (!formData.sku) {
      errors.sku = "SKU is required";
    }
    if (!formData.warehouse) {
      errors.warehouse = "Warehouse is required";
    }
    if (!formData.safety_stock_count) {
      errors.safety_stock_count = "Safety stock count is required";
    } else if (formData.safety_stock_count < 0) {
      errors.safety_stock_count = "Must be positive";
    }
    if (!formData.avg_fullfillment_days) {
      errors.avg_fullfillment_days = "Fulfillment days required";
    }
    if (!formData.avg_fullfillment_days_supplier) {
      errors.avg_fullfillment_days_supplier =
        "Supplier fulfillment days required";
    }
    if (!formData.min_order_quantity) {
      errors.min_order_quantity = "Minimum order quantity is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      ...formData,
      sku: formData.sku.sku || "",
      warehouse: formData.warehouse?._id || formData.warehouse,
      product: fetchedProduct?._id,
      client_id: fetchedProduct?.client_id?._id || clientId,
      supplier: fetchedProduct?.vendor?._id || fetchedProduct?.vendor,
    };

    try {
      if (selectedInventoryConfig) {
        await inventoryConfigService.edit(selectedInventoryConfig._id, payload);
        toast.success("Inventory config updated.");
      } else {
        await inventoryConfigService.create(payload);
        toast.success("Inventory config created.");
      }
      onAddData && onAddData();
      handleClose();
      setFormData(initData);
    } catch (e) {
      toast.error("Failed to save inventory config.");
      console.error(e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
        setFormData(initData);
      }}
      maxWidth="lg"
      fullWidth
      aria-labelledby="form-dialog-title"
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle
          id="form-dialog-title"
          style={{
            fontFamily: "Poppins",
            fontSize: 24,
            fontWeight: 500,
            color: "var(--text-primary)",
          }}
        >
          {selectedInventoryConfig ? "Edit" : "Create"} Inventory/Supplier
          Configuration
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 5 }} />

      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <FormLabel sx={labelStyle}>Supplier</FormLabel>
            <TextField
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              value={fetchedProduct?.vendor?.name || ""}
              onChange={(e) => {}}
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormLabel sx={labelStyle} required>
              SKU
            </FormLabel>
            <AutocompleteWithPagination
              hideLabel
              backgroundColor="var(--bg-paper)"
              customLabel="SKU"
              selectedItem={formData.sku ? formData.sku : null}
              onDataChange={(selectedSku) =>
                handleInputChange("sku", selectedSku)
              }
              width="100%"
              optionKey="sku"
              data={
                Array.isArray(fetchedProduct?.variants)
                  ? fetchedProduct.variants
                  : []
              }
              errorMessage={formErrors.sku}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormLabel sx={labelStyle} required>
              Warehouse
            </FormLabel>
            <AutocompleteWithPagination
              selectedItem={formData.warehouse}
              hideLabel
              onDataChange={(warehouse) =>
                handleInputChange("warehouse", warehouse)
              }
              width="100%"
              optionKey={(item) => `${item.name} (${item.warehouse_code})`}
              selectedClient={
                clientId ? { _id: clientId } : fetchedProduct?.client_id
              }
              apiEndPoint={(limit, skip, search, client) =>
                wmsService.fetch(client, limit, skip)
              }
              errorMessage={formErrors.warehouse}
              placeholder="Select Warehouse"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormLabel sx={labelStyle} required>
              Safety Stock Count
            </FormLabel>
            <TextField
              type="number"
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              value={formData.safety_stock_count}
              onChange={(e) =>
                handleInputChange(
                  "safety_stock_count",
                  Math.max(0, parseInt(e.target.value) || 0),
                )
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              placeholder="Enter safety stock count"
              error={!!formErrors.safety_stock_count}
              helperText={formErrors.safety_stock_count}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormLabel sx={labelStyle} required>
              Supplier Fulfillment Days
            </FormLabel>
            <TextField
              type="number"
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              value={formData.avg_fullfillment_days_supplier}
              onChange={(e) =>
                handleInputChange(
                  "avg_fullfillment_days_supplier",
                  Math.max(0, parseInt(e.target.value) || 0),
                )
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              placeholder="Supplier fulfillment days"
              error={!!formErrors.avg_fullfillment_days_supplier}
              helperText={formErrors.avg_fullfillment_days_supplier}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormLabel sx={labelStyle} required>
              Transportation & Inbound Receiving Days
            </FormLabel>
            <TextField
              type="number"
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              value={formData.avg_fullfillment_days}
              onChange={(e) =>
                handleInputChange(
                  "avg_fullfillment_days",
                  Math.max(0, parseInt(e.target.value) || 0),
                )
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              placeholder="Transportation & inbound receiving days"
              error={!!formErrors.avg_fullfillment_days}
              helperText={formErrors.avg_fullfillment_days}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormLabel sx={labelStyle} required>
              Minimum Order Quantity
            </FormLabel>
            <TextField
              type="number"
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              value={formData.min_order_quantity}
              onChange={(e) =>
                handleInputChange(
                  "min_order_quantity",
                  Math.max(1, parseInt(e.target.value) || 1),
                )
              }
              inputProps={{ min: 1, step: 1 }}
              fullWidth
              placeholder="Enter minimum order quantity"
              error={!!formErrors.min_order_quantity}
              helperText={formErrors.min_order_quantity}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleInputChange("is_active", e.target.checked)
                  }
                  sx={{
                    color: "#0040FF",
                    "&.Mui-checked": {
                      color: "#0040FF",
                    },
                  }}
                />
              }
              label={
                <CustomTypography
                  style={{
                    fontSize: 14,
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    color: "var(--text-heading)",
                  }}
                >
                  Active
                </CustomTypography>
              }
              sx={{ mt: 3 }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ paddingBottom: 15, paddingRight: 15 }}>
        <Button
          style={{
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            color: "primary",
          }}
          variant="outlined"
          onClick={() => {
            handleClose();
            setFormData(initData);
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          style={{
            color: "#FFFFFF",
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            width: 100,
            textTransform: "none",
          }}
        >
          {selectedInventoryConfig ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryConfigModal;
